import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Checkbox,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getGlobalFunnels, applyGlobalFunnel, likeGlobalFunnel, deleteGlobalFunnel, updateGlobalFunnel } from 'API/global-funnels';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useNavigate } from 'react-router';
import { useUserData } from 'hooks/use-user-data';
import { getAllLinkedinAccounts } from 'API/linkedin-accounts';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiButton from 'features/ui/base-components/pai-button';
import { FunnelTypes } from 'features/co-pilot/co-pilot.types';
import GlobalFunnelCard from 'features/store/global-funnel-card';
import FunnelDetailsDrawer from 'features/store/funnel-details-drawer';
import StoreBlockEditor from 'features/store/store-block-editor';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Roles } from 'enums';
import IconButton from '@mui/material/IconButton';

/** Neutral outline buttons — the pagination must not compete with the gradient CTA on every card. */
const paginationButtonSx = {
  height: 36,
  px: '16px',
  border: '1px solid #E3E3E8',
  backgroundColor: '#FFF',
  borderRadius: '8px',
  fontSize: '14px',
  textTransform: 'none',
  color: 'greys.medium',
  '&:hover': { backgroundColor: '#FFF', borderColor: 'primary.main' },
} as const;

const UseCaseStore: React.FC = () => {
  const [funnels, setFunnels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [successSnackbar, setSuccessSnackbar] = useState('');
  const [errorSnackbar, setErrorSnackbar] = useState('');
  const [selectedFunnel, setSelectedFunnel] = useState<any | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [likeLoading, setLikeLoading] = useState<string | null>(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applyFunnel, setApplyFunnel] = useState<any | null>(null);
  const [applyName, setApplyName] = useState('');
  const [applyLinkedinAccount, setApplyLinkedinAccount] = useState('');
  const [linkedinAccounts, setLinkedinAccounts] = useState<any[]>([]);
  const [applyError, setApplyError] = useState('');
  const [applyLoadingModal, setApplyLoadingModal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; loading: boolean; funnel: any | null }>({
    open: false,
    loading: false,
    funnel: null,
  });
  type EditDialogState = {
    open: boolean;
    loading: boolean;
    funnel: any | null;
    name: string;
    description: string;
    conversionRate: string;
  };

  const [editDialog, setEditDialog] = useState<EditDialogState>({
    open: false,
    loading: false,
    funnel: null,
    name: '',
    description: '',
    conversionRate: '',
  });

  const [channels, setChannels] = useState<string[]>([]);
  const [liProductType, setLiProductType] = useState('');
  const [minLikes, setMinLikes] = useState('');
  const [minUses, setMinUses] = useState('');
  const [language, setLanguage] = useState('');
  const [filterMenu, setFilterMenu] = useState<{ id: string; el: HTMLElement } | null>(null);

  // Pagination
  const limit = 10;
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { role, id: userId } = useUserData() as any;

  useEffect(() => {
    fetchFunnels();
  }, [search, channels, liProductType, minLikes, minUses, page]);

  const fetchFunnels = async () => {
    setLoading(true);
    try {
      const query: any = { q: search, offset: (page - 1) * limit, limit };
      if (channels.length) query.channels = channels.join(',');
      if (liProductType) query.liProductType = liProductType;
      if (minLikes) query.minLikes = Number(minLikes);
      if (minUses) query.minUses = Number(minUses);

      const res = await getGlobalFunnels(query);
      const fetched = res.data?.funnels || [];
      const processed = fetched.map((f: any) => ({
        ...f,
        liked: Array.isArray(f.likedBy) ? f.likedBy.includes(userId) : false,
      }));
      setFunnels(processed);
      setHasNext(fetched.length === limit);
    } catch {
      setErrorSnackbar(t('useCaseStoreLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLikeFunnel = async (id: string) => {
    setLikeLoading(id);
    try {
      const res = await likeGlobalFunnel(id);
      const { likes, liked } = res.data;
      setFunnels((prev) =>
        prev.map((f) => (f._id === id ? { ...f, likes, liked } : f))
      );
      if (selectedFunnel && selectedFunnel._id === id) {
        setSelectedFunnel({ ...selectedFunnel, likes, liked });
      }
    } catch {
      setErrorSnackbar(t('useCaseStoreLikeFailed'));
    } finally {
      setLikeLoading(null);
    }
  };

  const handleOpenDetailsDrawer = (funnel: any) => {
    setSelectedFunnel(funnel);
    setDetailsDrawerOpen(true);
  };

  const handleCloseDetailsDrawer = () => {
    setDetailsDrawerOpen(false);
    setSelectedFunnel(null);
  };

  const openApplyDialog = async (funnel: any) => {
    setApplyFunnel(funnel);
    setApplyName(`${funnel.name} (Copy)`);
    setApplyLinkedinAccount('');
    setApplyError('');
    setApplyDialogOpen(true);
    // Fetch LinkedIn accounts
    try {
      const res = await getAllLinkedinAccounts();
      setLinkedinAccounts(res.allLinkedInAccounts || []);
    } catch {
      setLinkedinAccounts([]);
    }
  };

  const closeApplyDialog = () => {
    setApplyDialogOpen(false);
    setApplyFunnel(null);
    setApplyName('');
    setApplyLinkedinAccount('');
    setApplyError('');
  };

  const handleApplyFunnelSubmit = async () => {
    if (!applyName.trim() || !applyLinkedinAccount) {
      setApplyError(t('useCaseStoreApplyValidation'));
      return;
    }
    setApplyLoadingModal(true);
    setApplyError('');
    try {
      const res = await applyGlobalFunnel(applyFunnel._id, {
        name: applyName.trim(),
        linkedinAccount: applyLinkedinAccount,
      });
      setSuccessSnackbar(t('useCaseStoreApplySuccess'));
      closeApplyDialog();
      if (res && res.data && res.data._id) {
        if (res.data.type === FunnelTypes.SOCIAL_SELLING_CO_PILOT) {
          setTimeout(() => navigate(`/lead-booster/co-pilot?_id=${res.data._id}&stepIndex=0`), 1000);
        } else {
          setTimeout(() => navigate(`/recruiting-booster/co-pilot?_id=${res.data._id}`), 1000);
        }
      }
    } catch (e: any) {
      // The API nests its message under `error.message`; reading only `data.message` meant
      // every real cause was replaced by the generic fallback text.
      setApplyError(
        e?.response?.data?.error?.message || e?.response?.data?.message || t('useCaseStoreApplyFailed')
      );
    } finally {
      setApplyLoadingModal(false);
    }
  };

  const handleOpenDeleteDialog = (funnel: any) => {
    setDeleteDialog({ open: true, loading: false, funnel });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, loading: false, funnel: null });
  };

  const handleDeleteFunnel = async () => {
    if (!deleteDialog.funnel) return;
    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      await deleteGlobalFunnel(deleteDialog.funnel._id);
      setFunnels((prev) => prev.filter((f) => f._id !== deleteDialog.funnel!._id));
      setSuccessSnackbar(t('useCaseStoreDeleteSuccess'));
      handleCloseDeleteDialog();
    } catch {
      setErrorSnackbar(t('useCaseStoreDeleteFailed'));
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleOpenEditDialog = (funnel: any) => {
    setEditDialog({
      open: true,
      loading: false,
      funnel,
      name: funnel.name || '',
      description: funnel.description || '',
      conversionRate: String(funnel.conversionRate || ''),
    });
  };

  const handleCloseEditDialog = () => {
    setEditDialog({ open: false, loading: false, funnel: null, name: '', description: '', conversionRate: '' });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.funnel) return;
    if (!editDialog.name.trim()) {
      setErrorSnackbar(t('useCaseStoreNameRequired'));
      return;
    }
    const conv = parseFloat(editDialog.conversionRate);
    if (isNaN(conv)) {
      setErrorSnackbar(t('useCaseStoreConversionMustBeNumber'));
      return;
    }
    setEditDialog((prev) => ({ ...prev, loading: true }));
    try {
      await updateGlobalFunnel(editDialog.funnel._id, {
        name: editDialog.name.trim(),
        description: editDialog.description,
        conversionRate: conv,
      } as any);
      setFunnels((prev) =>
        prev.map((f) =>
          f._id === editDialog.funnel!._id
            ? { ...f, name: editDialog.name.trim(), description: editDialog.description, conversionRate: conv }
            : f
        )
      );
      setSuccessSnackbar(t('useCaseStoreUpdateSuccess'));
      handleCloseEditDialog();
    } catch {
      setErrorSnackbar(t('useCaseStoreUpdateFailed'));
      setEditDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  /**
   * Language of a template, derived from its name suffix.
   *
   * There is no language field on the model — the German and English variants are only
   * distinguishable by the "(DE)" / "(EN)" suffix every template carries (verified against
   * production: 14 DE, 14 EN, none without). So this filter is a display-level heuristic,
   * deliberately client-side, and anything unrecognised stays visible instead of being
   * hidden. Once the model gains a real `language` field this should move into the query.
   */
  const languageOf = (funnel: any): 'de' | 'en' | null => {
    const name = String(funnel?.name ?? '');
    if (/\(DE\)\s*$/i.test(name)) return 'de';
    if (/\(EN\)\s*$/i.test(name)) return 'en';
    return null;
  };

  const visibleFunnels = useMemo(
    () => (language ? funnels.filter((f) => languageOf(f) !== (language === 'de' ? 'en' : 'de')) : funnels),
    [funnels, language],
  );

  const maxLikes = visibleFunnels.length
    ? Math.max(...visibleFunnels.map((f) => Number(f.likes ?? f.likeCount ?? 0)))
    : 0;
  const maxConversion = visibleFunnels.length
    ? Math.max(...visibleFunnels.map((f) => f.conversionRate || 0))
    : 0;

  const isAdmin = role === Roles.ADMIN || role === Roles.OWNER;

  const channelLabel = channels.length ? channels.join(', ') : t('useCaseStoreAny');
  const productLabel = liProductType
    ? liProductType === 'NORMAL'
      ? 'Normal'
      : 'SalesNav'
    : t('useCaseStoreAny');
  const languageLabel = language
    ? language === 'de'
      ? t('useCaseStoreLanguageDe')
      : t('useCaseStoreLanguageEn')
    : t('useCaseStoreLanguageAny');

  /**
   * The six separate form fields collapsed into one chip row: each chip states its current
   * value and opens the very same options in a menu, so the filter state and the query it
   * produces are untouched — only the control that sets them changed.
   */
  const filterChips: { id: string; label: string; active: boolean }[] = [
    { id: 'channels', label: `${t('useCaseStoreChannels')}: ${channelLabel}`, active: channels.length > 0 },
    { id: 'product', label: `${t('useCaseStoreLinkedinProduct')}: ${productLabel}`, active: !!liProductType },
    { id: 'language', label: `${t('useCaseStoreLanguage')}: ${languageLabel}`, active: !!language },
    { id: 'minLikes', label: minLikes ? `${t('useCaseStoreMinLikes')}: ${minLikes}` : t('useCaseStoreMinLikes'), active: !!minLikes },
    { id: 'minUses', label: minUses ? `${t('useCaseStoreMinUses')}: ${minUses}` : t('useCaseStoreMinUses'), active: !!minUses },
  ];

  const closeFilterMenu = () => setFilterMenu(null);

  return (
    <Stack
      sx={{
        height: '100%',
        overflowY: 'auto',
        backgroundColor: (theme) => theme.palette.backgrounds.main_new,
      }}
    >
      {/* Header — title, template count and search share one line instead of the search
          field competing with five filter fields for the same row. */}
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={3}
        flexWrap="wrap"
        sx={{ p: '28px 32px 16px 32px' }}
      >
        <Stack gap="6px">
          <Typography sx={{ fontSize: '26px', fontWeight: 500, letterSpacing: '-0.2px' }}>
            {t('useCaseStoreTitle')}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: (theme) => theme.palette.greys.medium }}>
            {t('useCaseStoreSubtitle', { count: visibleFunnels.length })}
          </Typography>
        </Stack>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('useCaseStoreSearchPlaceholder')}
          inputProps={{ 'aria-label': t('useCaseStoreSearchLabel') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '18px', color: (theme) => theme.palette.greys.medium }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 340,
            '& .MuiOutlinedInput-root': {
              height: 40,
              backgroundColor: '#FFF',
              borderRadius: '8px',
              fontSize: '15px',
              '& fieldset': { borderColor: '#E3E3E8' },
              '&:hover fieldset': { borderColor: '#E3E3E8' },
              '&.Mui-focused fieldset': { borderColor: (theme) => theme.palette.primary.main, borderWidth: 1 },
            },
          }}
        />
      </Stack>

      {/* Filter chip row */}
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ p: '0 32px 20px 32px' }}>
        <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.greys.medium, mr: '4px' }}>
          {t('useCaseStoreFilterLabel')}
        </Typography>
        {filterChips.map((chip) => (
          <Box
            key={chip.id}
            component="button"
            type="button"
            onClick={(e: React.MouseEvent<HTMLElement>) => setFilterMenu({ id: chip.id, el: e.currentTarget })}
            sx={{
              height: 36,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: '12px',
              borderRadius: '18px',
              fontFamily: 'inherit',
              fontSize: '13px',
              cursor: 'pointer',
              border: (theme) => `1px solid ${chip.active ? theme.palette.primary.main : '#E3E3E8'}`,
              backgroundColor: (theme) => (chip.active ? theme.palette.backgrounds.main : '#FFF'),
              color: (theme) => (chip.active ? theme.palette.primary.main : '#555'),
              '&:hover': { borderColor: (theme) => theme.palette.primary.main },
            }}
          >
            {chip.label}
            <KeyboardArrowDownIcon sx={{ fontSize: '16px' }} />
          </Box>
        ))}
        <Typography sx={{ ml: 'auto', fontSize: '13px', color: (theme) => theme.palette.greys.medium }}>
          {t('useCaseStoreSortedByLikes')}
        </Typography>
      </Stack>

      <Menu anchorEl={filterMenu?.el ?? null} open={filterMenu?.id === 'channels'} onClose={closeFilterMenu}>
        {['linkedin', 'email', 'phone'].map((ch) => (
          <MenuItem
            key={ch}
            onClick={() =>
              setChannels((prev) => (prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]))
            }
          >
            <Checkbox size="small" checked={channels.includes(ch)} sx={{ p: 0, mr: 1 }} />
            <ListItemText primary={ch} />
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={filterMenu?.el ?? null} open={filterMenu?.id === 'product'} onClose={closeFilterMenu}>
        {[
          { value: '', label: t('useCaseStoreAny') },
          { value: 'NORMAL', label: 'Normal' },
          { value: 'SALESNAV', label: 'SalesNav' },
        ].map((option) => (
          <MenuItem
            key={option.value || 'any'}
            selected={liProductType === option.value}
            onClick={() => {
              setLiProductType(option.value);
              closeFilterMenu();
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={filterMenu?.el ?? null} open={filterMenu?.id === 'language'} onClose={closeFilterMenu}>
        {[
          { value: '', label: t('useCaseStoreLanguageAny') },
          { value: 'de', label: t('useCaseStoreLanguageDe') },
          { value: 'en', label: t('useCaseStoreLanguageEn') },
        ].map((option) => (
          <MenuItem
            key={option.value || 'any'}
            selected={language === option.value}
            onClick={() => {
              setLanguage(option.value);
              closeFilterMenu();
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={filterMenu?.el ?? null} open={filterMenu?.id === 'minLikes'} onClose={closeFilterMenu}>
        <Box sx={{ px: 2, py: 1 }}>
          <PaiTextField
            label={t('useCaseStoreMinLikes')}
            value={minLikes}
            onChange={(e) => setMinLikes(e.target.value)}
            type="number"
            variant="background"
            style={{ width: 180 }}
          />
        </Box>
      </Menu>

      <Menu anchorEl={filterMenu?.el ?? null} open={filterMenu?.id === 'minUses'} onClose={closeFilterMenu}>
        <Box sx={{ px: 2, py: 1 }}>
          <PaiTextField
            label={t('useCaseStoreMinUses')}
            value={minUses}
            onChange={(e) => setMinUses(e.target.value)}
            type="number"
            variant="background"
            style={{ width: 180 }}
          />
        </Box>
      </Menu>

      {/* Funnel Grid */}
      <Box sx={{ p: '0 32px 32px 32px' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : visibleFunnels.length === 0 ? (
          // Previously an active filter combination just rendered an empty white area with
          // no indication that anything had been filtered out.
          <Box display="flex" justifyContent="center" mt={6}>
            <Typography variant="body2" color="text.secondary">
              {t('useCaseStoreEmpty')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              // A grid instead of a wrapping flex row: rows share a height, so cards line
              // up instead of ending at whatever their own text happens to need.
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: '16px',
              alignItems: 'stretch',
            }}
          >
            {visibleFunnels.map((funnel) => (
              <GlobalFunnelCard
                key={funnel._id}
                funnel={funnel}
                isMostPopular={funnel.likes === maxLikes && maxLikes > 0}
                isBestConversion={funnel.conversionRate === maxConversion && maxConversion > 0}
                likeLoading={likeLoading === funnel._id}
                adminActions={
                  isAdmin ? (
                    <>
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleOpenEditDialog(funnel)}>
                        <EditIcon color="primary" fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleOpenDeleteDialog(funnel)}>
                        <DeleteIcon color="error" fontSize="small" />
                      </IconButton>
                    </>
                  ) : undefined
                }
                onOpenDetails={handleOpenDetailsDrawer}
                onLike={handleLikeFunnel}
                onApply={openApplyDialog}
              />
            ))}
          </Box>
        )}

        {/* Pagination controls */}
        <Stack direction="row" alignItems="center" justifyContent="center" mt="32px" gap="16px">
          <Button
            disableElevation
            sx={paginationButtonSx}
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('useCaseStorePrevious')}
          </Button>
          <Typography sx={{ fontSize: '14px', color: (theme) => theme.palette.greys.medium }}>
            {t('useCaseStorePage', { page })}
          </Typography>
          <Button
            disableElevation
            sx={{ ...paginationButtonSx, color: (theme) => theme.palette.primary.main }}
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('useCaseStoreNext')}
          </Button>
        </Stack>
      </Box>

      <Paper>
        <StoreBlockEditor />
      </Paper>

      {/* Funnel Details Drawer */}
      <FunnelDetailsDrawer
        open={detailsDrawerOpen}
        funnel={selectedFunnel}
        likeLoading={likeLoading === selectedFunnel?._id}
        isMostPopular={selectedFunnel ? selectedFunnel.likes === maxLikes && maxLikes > 0 : false}
        isBestConversion={selectedFunnel ? selectedFunnel.conversionRate === maxConversion && maxConversion > 0 : false}
        onClose={handleCloseDetailsDrawer}
        onLike={handleLikeFunnel}
        onApply={(funnel: any) => {
          openApplyDialog(funnel);
          handleCloseDetailsDrawer();
        }}
      />

      {/* Apply Global Funnel Modal */}
      <Dialog open={applyDialogOpen} onClose={closeApplyDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('useCaseStoreApplyTitle')}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <PaiTextField
              label={t('useCaseStoreCampaignName')}
              value={applyName}
              onChange={e => setApplyName(e.target.value)}
              disabled={applyLoadingModal}
              variant="background"
            />
            <PaiSelect
              label={t('useCaseStoreLinkedinAccount')}
              value={applyLinkedinAccount}
              onChange={e => setApplyLinkedinAccount(e.target.value)}
              disabled={applyLoadingModal}
              variant="background"
            >
              {linkedinAccounts.map((acc: any) => (
                <PaiMenuItem key={acc._id} value={acc._id}>
                  {acc.userEmail}
                </PaiMenuItem>
              ))}
            </PaiSelect>
            {applyError && <Alert severity="error">{applyError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <PaiButton text={t('cancel')} border="rounded" size="small" color="secondary" action={closeApplyDialog} disabled={applyLoadingModal} />
          <PaiButton text={t('useCaseStoreApply')} border="rounded" size="small" action={handleApplyFunnelSubmit} disabled={applyLoadingModal || !applyName.trim() || !applyLinkedinAccount} loading={applyLoadingModal} />
        </DialogActions>
      </Dialog>

      {/* Edit Funnel Dialog */}
      <Dialog open={editDialog.open} onClose={handleCloseEditDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('useCaseStoreEditTitle')}</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <PaiTextField
              label={t('name')}
              value={editDialog.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditDialog((prev) => ({ ...prev, name: e.target.value }))
              }
            />
            <PaiTextArea
              label={t('useCaseStoreDescription')}
              value={editDialog.description}
              rows={4}
              onChange={(e) => setEditDialog((prev) => ({ ...prev, description: e.target.value }))}
            />
            <PaiTextField
              label={t('useCaseStoreConversionRate')}
              value={editDialog.conversionRate}
              type="number"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditDialog((prev) => ({ ...prev, conversionRate: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <PaiButton text={t('cancel')} border="rounded" size="small" color="secondary" action={handleCloseEditDialog} disabled={editDialog.loading} />
          <PaiButton text={t('save')} border="rounded" size="small" action={handleSaveEdit} loading={editDialog.loading} />
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('useCaseStoreDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('useCaseStoreDeleteConfirm', { name: deleteDialog.funnel?.name ?? '' })}</Typography>
        </DialogContent>
        <DialogActions>
          <PaiButton text={t('cancel')} border="rounded" size="small" color="secondary" action={handleCloseDeleteDialog} disabled={deleteDialog.loading} />
          <PaiButton text={t('delete')} border="rounded" size="small" color="error" action={handleDeleteFunnel} loading={deleteDialog.loading} />
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!successSnackbar}
        autoHideDuration={4000}
        onClose={() => setSuccessSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessSnackbar('')} severity="success" sx={{ width: '100%' }}>
          {successSnackbar}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={4000}
        onClose={() => setErrorSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorSnackbar('')} severity="error" sx={{ width: '100%' }}>
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default UseCaseStore;